import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { RefreshCcw, RotateCcw, RotateCw, X, ZoomIn, ZoomOut } from 'lucide-react'
import { useImageTransform, useLockBodyScroll } from '../../../hooks'
import { cn } from '../../../utils'

type ImagePreviewModalProps = {
  open: boolean
  src: string
  alt: string
  onClose: () => void
}

/**
 * 图片预览弹层：支持缩放、旋转、拖拽平移、点击关闭。
 */
export function ImagePreviewModal({ open, src, alt, onClose }: ImagePreviewModalProps) {
  const transform = useImageTransform({ minScale: 0.2, maxScale: 5, step: 0.15 })
  const {
    scale,
    rotate,
    zoomIn,
    zoomOut,
    rotateLeft,
    rotateRight,
    reset,
    onWheel,
    onPointerDown,
    onPointerMove,
    setImageElement,
    shouldIgnoreClick,
    onPointerUp,
  } = transform

  useLockBodyScroll(open)

  useEffect(() => {
    if (!open) return
    reset()
  }, [open, src, reset])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={alt || '图片预览'}
    >
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          className="relative w-full h-full flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="absolute top-4 right-4 z-30 flex items-center gap-2 pointer-events-auto">
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20',
                'h-10 w-10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
              )}
              onClick={zoomOut}
              title="缩小"
            >
              <ZoomOut className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20',
                'h-10 w-10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
              )}
              onClick={zoomIn}
              title="放大"
            >
              <ZoomIn className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20',
                'h-10 w-10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
              )}
              onClick={rotateLeft}
              title="左旋转"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20',
                'h-10 w-10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
              )}
              onClick={rotateRight}
              title="右旋转"
            >
              <RotateCw className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20',
                'h-10 w-10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
              )}
              onClick={reset}
              title="重置"
            >
              <RefreshCcw className="w-5 h-5" />
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20',
                'h-10 w-10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
              )}
              onClick={onClose}
              title="关闭"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 text-xs text-white/80 pointer-events-none">
            {Math.round(scale * 100)}% · {((rotate % 360) + 360) % 360}°
          </div>

          <div
            className={cn(
              'flex items-center justify-center w-full h-full z-10',
              'touch-none select-none'
            )}
            onWheel={onWheel}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={reset}
          >
            <img
              ref={setImageElement}
              src={src}
              alt={alt}
              draggable={false}
              className="max-w-[92vw] max-h-[84vh]"
              onClick={() => {
                if (shouldIgnoreClick()) return
                onClose()
              }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
